/**
 * The plot's own sun: the season's flux against the body's mean, the
 * light-rig scale it drives, and `applySun`, which turns the scrub's
 * declination and hour angle into the walkaround's sun direction, the
 * plot's overlay wash and its contour lines.
 */

import * as THREE from 'three';

import type { BodySceneHandle } from './bodyScene.ts';
import { solarFluxWm2, sunAngles } from './plotField.ts';
import type { SolarScrubHandle } from './solarScrub.ts';
import type { AtlasLive } from '../atlas';
import type { BodyReadersHandle } from './bodyReaders.ts';
import { SOLAR_RADIUS_KM } from './sol.ts';
import { createSunCover, type SunCoverHandle } from './sunCover.ts';
import type { WorldVec } from '../world/frame.ts';
import type { BodyRow } from '../world/types';

export interface PlotSunDeps {
  readonly live: AtlasLive;
  readonly bodyReaders: BodyReadersHandle;
  readonly solarScrub: SolarScrubHandle;
  readonly GRID_STAGES: readonly { fill: number; cells: number; parcels: number }[];
  closeness(): number;
  readonly bodyScene: BodySceneHandle;
  readonly skyDebug: { progradeSpin: boolean };
  inLocalSky(row: BodyRow): boolean;
}

/** `setSunScale`'s own state; the orrery light rig's scale for this frame. */
export interface PlotSunState {
  readonly sunScale: number;
}

/** The inputs and altitude of the last sun `applySun` placed, degrees and radians as named. */
export interface PlacedSun {
  latDeg: number;
  decDeg: number;
  hourAngleRad: number;
  altDeg: number;
  lsDeg: number;
  obliquityDeg: number;
}

export interface PlotSunHandle {
  seasonLight(): number;
  /** `groundShare` at `groundWeight` is the part of `scale` that is `cover.ground` (`orrery.setSunScale`). */
  setSunScale(scale: number, groundShare?: number, groundWeight?: number): void;
  /** What of the Sun the bodies in the eye's sky leave it (`sunCover.ts`). */
  readonly cover: SunCoverHandle;
  /** The frame's cover from the eye it is drawn from; re-lights the plot when the ground's share moves. */
  updateCover(eye: WorldVec): void;
  localSunAzimuth(az: number): number;
  applySun(): void;
  readonly plotSunDirLocal: THREE.Vector3;
  /** Null until `applySun` has placed one; a preview's sun while a preview is up. */
  readonly placed: Readonly<PlacedSun> | null;
  readonly state: PlotSunState;
}

export function createPlotSun(deps: PlotSunDeps): PlotSunHandle {
  const { closeness, bodyScene } = deps;
  const { bodyClimate } = deps.bodyReaders;

  /**
   * The focused body's sunlight at this season over its mean, the one factor
   * both tiers of its ground take the season from: the plot's `uSunFlux`, and
   * the orrery's light rig below the SYSTEM band, which lights the leaves.
   */
  function seasonLight(): number {
    const climate = deps.live.plot ? deps.live.plot.climate : bodyClimate();
    if (!climate || !(climate.fluxMeanWm2 > 0)) return 1;
    return solarFluxWm2(deps.solarScrub.solar.lsDeg, climate) / climate.fluxMeanWm2;
  }
  /** The scale the orrery's light rig is drawn at this frame; see `seasonLight`. */
  let sunScale = 1;
  function setSunScale(scale: number, groundShare = 1, groundWeight = 0): void {
    sunScale = scale;
    bodyScene.state.orrery?.setSunScale(scale, groundShare, groundWeight);
  }

  const cover = createSunCover({
    orrery: () => bodyScene.state.orrery,
    focus: () => bodyScene.state.bodyNode,
    inLocalSky: (row) => deps.inLocalSky(row),
    sunRadiusM: SOLAR_RADIUS_KM * 1000,
  });
  let litGround = 1;
  function updateCover(eye: WorldVec): void {
    cover.update(eye);
    bodyScene.state.orrery?.sol.setVisibleShare(cover.skyEye);
    if (cover.ground !== litGround) {
      litGround = cover.ground;
      applySun();
    }
  }

  /**
   * The sun in the PLOT's own frame, which is the sky's frame inside the claim.
   *
   * The walkaround is drawn in a local y-up frame (`surfaceRegime`), not on the
   * globe, so above the claim the sky is given the body's true radial and
   * inside it this vector - the same one `applySun` hands the terrain, so the
   * two cannot point different ways.
   */
  const plotSunDirLocal = new THREE.Vector3(0, 1, 0);
  let placed: PlacedSun | null = null;

  /**
   * The sun's azimuth in the LOCAL frame, +x east and 0 toward -z.
   *
   * `sunAngles` puts an advancing hour angle's afternoon sun in the west,
   * which is a prograde rotator's; a negative `W_rate_deg_day` turns the other
   * way, so its sun crosses west to east and the azimuth mirrors about the
   * meridian. `descent-dojo --sky --inject prograde-spin` ignores the sign.
   */
  function localSunAzimuth(az: number): number {
    const rate = bodyScene.state.bodyNode?.row.rotation.W_rate_deg_day ?? 0;
    return rate < 0 && !deps.skyDebug.progradeSpin ? 2 * Math.PI - az : az;
  }

  function applySun(): void {
    deps.live.needsRender = true;
    // The sun direction FIRST, and off the body's own climate where no plot has
    // loaded: `updateAtmoSky` takes `plotSunDirLocal` for every surface-regime
    // frame, so a body with no site left it at its (0, 1, 0) initialiser and
    // drew a permanent noon sky - which on Venus is a sub-cloud ceiling that
    // reads the same 12.3 grey levels with the sun 82 degrees below the horizon.
    // The site's centre, then the loaded plot's, then the LADDER's own aim -
    // which is the only one a body with no site has, and Venus has none.
    const lat = deps.live.plotField ? deps.live.plotField.meta.center_lat_deg : (deps.solarScrub.solarLatLon()?.lat ?? null);
    const climate = deps.live.plot ? deps.live.plot.climate : bodyClimate();
    if (lat === null || !climate) return;
    const dec = deps.solarScrub.sunDeclinationDeg(climate);
    const H = (deps.solarScrub.solar.solPhase - 0.5) * 2 * Math.PI;
    const { alt, az: azFromH } = sunAngles(lat, dec, H);
    placed = {
      latDeg: lat, decDeg: dec, hourAngleRad: H, altDeg: THREE.MathUtils.radToDeg(alt),
      lsDeg: deps.solarScrub.solar.lsDeg, obliquityDeg: climate.obliquityDeg,
    };
    const az = localSunAzimuth(azFromH);
    plotSunDirLocal.set(
      Math.cos(alt) * Math.sin(az),
      Math.sin(alt),
      -Math.cos(alt) * Math.cos(az),
    ).normalize();
    if (!deps.live.plot || !deps.live.plotField) return;
    deps.live.plot.setSun(alt, az, seasonLight() * cover.ground);
    const g = deps.GRID_STAGES[deps.solarScrub.solar.overlay] ?? deps.GRID_STAGES[2]!;
    // The verdict wash is a map legend, and a legend painted across ground you
    // are standing on stops being information and becomes a coloured filter
    // over the terrain. It answers "which of these plots should I take", which
    // is a question you ask from above — so it fades out as the camera drops in
    // among the rocks, while the property LINES, which say where you are, stay.
    const c = closeness();
    deps.live.plot.setOverlay(g.fill * (1 - c), g.cells * (1 - c), g.parcels);
    deps.live.plot.setContours(deps.solarScrub.solar.contour);
  }

  return {
    seasonLight,
    setSunScale,
    cover,
    updateCover,
    localSunAzimuth,
    applySun,
    plotSunDirLocal,
    get placed() { return placed; },
    state: {
      get sunScale() { return sunScale; },
    },
  };
}
